import React from 'react';
import { ChevronDown, ChevronUp, Volume2, Loader } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { FileType } from '../../../types';
import { getFileUrl } from '../../../lib/routingUtils';
import defaultThumbnail from '../../../assets/recording/DefaultThumbnail.svg';
import { Button } from '../../../components/ui/button';
import { cn } from '../../../lib/utils';

type AssistantFileType = FileType & {
  fileId?: string;
  fileName?: string;
};

interface FileListProps {
  files: AssistantFileType[];
  isExpanded: boolean;
  onToggleExpand: () => void;
  fileThumbnails: Array<{
    fileId: string;
    thumbnailUrl: string | null;
    isLoading: boolean;
  }>;
  setFileThumbnails: React.Dispatch<
    React.SetStateAction<
      Array<{
        fileId: string;
        thumbnailUrl: string | null;
        isLoading: boolean;
      }>
    >
  >;
  isLoading?: boolean;
  label?: string;
}

const FileList: React.FC<FileListProps> = ({
  files,
  isExpanded,
  onToggleExpand,
  fileThumbnails,
  setFileThumbnails,
  isLoading = false,
  label,
}) => {
  const { t } = useTranslation();

  return (
    <div className="mt-2">
      <Button
        onClick={onToggleExpand}
        variant="ghost"
        size="sm"
        className={cn(
          'w-full justify-start text-muted-foreground py-1 px-3 rounded-full',
          'flex gap-2 hover:bg-white dark:hover:bg-secondary',
        )}
      >
        {isExpanded ? (
          <ChevronUp className="w-4 h-4" />
        ) : (
          <ChevronDown className="w-4 h-4" />
        )}
        <div className="relative overflow-hidden inline-block rounded">
          <span className="text-xs px-1 relative z-[1]">
            {label || t('assistant.fileList.sources')}
          </span>

          {isLoading && (
            <div
              className={cn(
                'absolute inset-0 z-[2]',
                'bg-gradient-to-r from-transparent via-white/40 to-transparent',
                'dark:via-white/5',
              )}
              style={{
                animation: 'shimmer 1.5s infinite',
              }}
            />
          )}
        </div>
      </Button>

      {isExpanded && (
        <div className="px-0 mt-1">
          {files.map((file: AssistantFileType) => {
            const thumbnailData = fileThumbnails.find(
              (thumbnail) => thumbnail.fileId === (file.fileId || ''),
            );
            const isAudioOnly = file.streams?.includes('video') === false;
            const { fileId = '', teamId, parentId } = file;
            const fileUrl = getFileUrl({
              fileId,
              teamId,
              view: 'list',
              parentId,
              withBasePath: true,
            });

            return (
              <a
                key={file.fileId || file._id}
                href={fileUrl}
                target="_blank"
                rel="noreferrer"
                className={cn(
                  'flex items-center gap-3 p-2 rounded-lg',
                  'no-underline',
                  'hover:bg-black/5 dark:hover:bg-white/10',
                )}
              >
                <div
                  className={cn(
                    'w-12 h-12 min-w-[48px] rounded-xl overflow-hidden',
                    'bg-muted dark:bg-secondary flex justify-center items-center',
                    'hover:scale-105',
                  )}
                >
                  {((thumbnailData?.thumbnailUrl && !isAudioOnly) ||
                    (!thumbnailData?.isLoading && !isAudioOnly)) && (
                    <img
                      src={thumbnailData?.thumbnailUrl || defaultThumbnail}
                      alt={t('assistant.fileList.thumbnail', {
                        fileName: file.fileName || '',
                      })}
                      className={cn(
                        'w-full h-full object-cover',
                        !thumbnailData?.thumbnailUrl ||
                          thumbnailData?.thumbnailUrl === defaultThumbnail
                          ? 'dark:invert'
                          : '',
                      )}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        if (target.src !== defaultThumbnail) {
                          target.src = defaultThumbnail;
                          setFileThumbnails((prev) =>
                            prev.map((thumbnail) =>
                              thumbnail.fileId === (file.fileId || '')
                                ? {
                                    ...thumbnail,
                                    thumbnailUrl: defaultThumbnail,
                                    isLoading: false,
                                  }
                                : thumbnail,
                            ),
                          );
                        }
                      }}
                    />
                  )}
                  {!thumbnailData?.thumbnailUrl && thumbnailData?.isLoading && (
                    <div className="flex justify-center items-center h-full w-full bg-black/5 dark:bg-white/5">
                      <Loader className="w-6 h-6 animate-spin text-muted-foreground" />
                    </div>
                  )}
                  {!thumbnailData?.thumbnailUrl &&
                    !thumbnailData?.isLoading &&
                    isAudioOnly && (
                      <div className="flex justify-center items-center h-full w-full text-muted-foreground">
                        <Volume2 className="w-5 h-5" />
                      </div>
                    )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-foreground truncate">
                    {file.fileName || ''}
                  </div>
                  {file.duration && (
                    <div className="text-xs text-muted-foreground">
                      {Math.floor(file.duration / 60)}:
                      {String(Math.floor(file.duration % 60)).padStart(2, '0')}
                    </div>
                  )}
                </div>
              </a>
            );
          })}
        </div>
      )}

      <style>{`
        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
      `}</style>
    </div>
  );
};

export default FileList;
